import type { FC, HTMLAttributes } from 'react'

import { Box, type BoxProps } from '@/components/Box/Box'
import { Button } from '@/components/Button/Button'
import { Hairline } from '@/components/Hairline/Hairline'
import { Link } from '@/components/Link/Link'
import { BlockQuote } from '@/components/Text/BlockQuote'
import { Heading } from '@/components/Text/Heading'
import { Text } from '@/components/Text/Text'
import { type Components, RenderReact, type Root } from '@doist/content-system'
import { List } from './components/list/list'

import { IFrame } from './components/iframe/iframe'
import { Table } from './components/table/table'
import { Code, Em, Italic, Pre, Strong } from './components/text/text'
import css from './render-html.module.css'

interface CustomComponents extends Components {
    img: FC<HTMLAttributes<HTMLImageElement>>
    fragment: FC<HTMLAttributes<HTMLElement>>
}

type RenderHtmlProps = Omit<BoxProps, 'content'> & {
    components?: Components
    content?: Root
}

export const baseComponents: CustomComponents = {
    fragment: (props) => <>{props.children}</>,
    h1: (props) => <Heading size="major-lg" level="1" {...props} />,
    h2: (props) => <Heading size="major-base" level="2" {...props} />,
    h3: (props) => <Heading size="major-sm" level="3" {...props} />,
    h4: (props) => <Heading size="minor-lg" level="4" {...props} />,
    h5: (props) => <Heading size="minor-xs" level="5" {...props} />,
    p: (props) => <Text size="body-base" {...props} />,
    li: (props) => <Text tag="li" {...props} />,
    ul: (props) => <List tag="ul" {...props} />,
    ol: (props) => <List tag="ol" {...props} />,
    img: (props) => <img style={{ width: '100%' }} {...props} alt="" />,
    button: (props) => <Button {...props}>{props.children}</Button>,
    table: Table,
    a: Link,
    hr: Hairline,
    blockquote: BlockQuote,
    pre: Pre,
    strong: Strong,
    code: Code,
    em: Em,
    i: Italic,
    iframe: IFrame,
}

export function RenderHtml({ content, components, children, ...rest }: RenderHtmlProps) {
    const complete = { ...baseComponents, ...components }

    return (
        <Box className={css.contentWrapper} {...rest}>
            {content ? <RenderReact content={content} components={complete} /> : null}
            {children ? <>{children}</> : null}
        </Box>
    )
}
